import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, ChevronLeft, ChevronRight, Columns2, Grid3X3, Images, Volume2, VolumeX, X } from "lucide-react";
import { useTranslation } from "../i18n";
import { apiUrl, wsUrl } from "../api";
import LanguageSwitcher from "./LanguageSwitcher";
import "./Gallery.css";
import ImageUpload from "./ImageUpload";

const GALLERY_VIEWER_SESSION_KEY = "galleryViewerSessionId";
const FULLSCREEN_IMAGE_VARIANT_ORDER = ["small", "medium", "large"];
const GALLERY_IMAGE_PRELOAD_MARGIN = 1200;
const GALLERY_IMAGE_FAILSAFE_DELAY_MS = 1800;
const EMPTY_IMAGE_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

function createFallbackSessionId() {
  return `gallery-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getGalleryViewerSessionId() {
  try {
    const existingSessionId = window.localStorage.getItem(GALLERY_VIEWER_SESSION_KEY);
    if (existingSessionId) {
      return existingSessionId;
    }
  } catch (error) {
    console.warn("[gallery] unable to read viewer session id from localStorage", error);
  }

  try {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (error) {
    console.warn("[gallery] unable to create viewer session id with crypto.randomUUID", error);
  }

  return createFallbackSessionId();
}

function readVideoBufferedPercent(video) {
  if (!video?.buffered?.length || !Number.isFinite(video.duration) || video.duration <= 0) {
    return 0;
  }

  const currentTime = video.currentTime || 0;
  let bufferedEnd = 0;

  for (let index = 0; index < video.buffered.length; index += 1) {
    const rangeStart = video.buffered.start(index);
    const rangeEnd = video.buffered.end(index);
    if (currentTime >= rangeStart && currentTime <= rangeEnd) {
      bufferedEnd = rangeEnd;
      break;
    }
    bufferedEnd = Math.max(bufferedEnd, rangeEnd);
  }

  return Math.max(0, Math.min(100, (bufferedEnd / video.duration) * 100));
}

function LazyGalleryImage({ src, alt, className, eager = false }) {
  const imageRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(eager);

  useEffect(() => {
    if (shouldLoad) {
      return undefined;
    }

    const node = imageRef.current;
    if (!node) {
      return undefined;
    }

    let observer;

    const loadImage = () => {
      setShouldLoad(true);
    };

    const checkVisibility = () => {
      const rect = node.getBoundingClientRect();
      if (
        rect.bottom >= -GALLERY_IMAGE_PRELOAD_MARGIN &&
        rect.top <= window.innerHeight + GALLERY_IMAGE_PRELOAD_MARGIN
      ) {
        loadImage();
      }
    };

    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
            loadImage();
          }
        },
        { rootMargin: `${GALLERY_IMAGE_PRELOAD_MARGIN}px 0px` }
      );
      observer.observe(node);
    }

    checkVisibility();

    const handleViewportChange = () => {
      checkVisibility();
    };

    window.addEventListener("scroll", handleViewportChange, { passive: true });
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("orientationchange", handleViewportChange);
    document.addEventListener("visibilitychange", handleViewportChange);

    const failsafeTimeoutId = window.setTimeout(loadImage, GALLERY_IMAGE_FAILSAFE_DELAY_MS);

    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", handleViewportChange);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("orientationchange", handleViewportChange);
      document.removeEventListener("visibilitychange", handleViewportChange);
      window.clearTimeout(failsafeTimeoutId);
    };
  }, [shouldLoad]);

  return (
    <img
      ref={imageRef}
      src={shouldLoad ? src : EMPTY_IMAGE_SRC}
      alt={alt}
      className={`${className}${shouldLoad ? "" : " gallery-image-pending"}`}
      decoding="async"
      fetchPriority={eager ? "high" : "auto"}
    />
  );
}

function Gallery() {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(0);
  const [approvedImages, setApprovedImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [mobileLayout, setMobileLayout] = useState("grid");
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [showsMobileDock, setShowsMobileDock] = useState(false);
  const [isModalVideoMuted, setIsModalVideoMuted] = useState(false);
  const [fullscreenVideoState, setFullscreenVideoState] = useState({
    mediaId: "",
    status: "idle",
    error: "",
    bufferedPercent: 0,
  });
  const [loadedFullscreenImageVariants, setLoadedFullscreenImageVariants] = useState({});
  const [pendingUploadIds, setPendingUploadIds] = useState([]);
  const [newlyApprovedIds, setNewlyApprovedIds] = useState([]);
  const [approvalFeedback, setApprovalFeedback] = useState(null);
  const pausedThumbnailVideosRef = useRef([]);
  const modalVideoRef = useRef(null);
  const imageUploadRef = useRef(null);
  const websocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const galleryViewerSessionIdRef = useRef(getGalleryViewerSessionId());
  const selectedImageIdRef = useRef("");

  useEffect(() => {
    try {
      window.localStorage.setItem(GALLERY_VIEWER_SESSION_KEY, galleryViewerSessionIdRef.current);
    } catch (error) {
      console.warn("[gallery] unable to persist viewer session id to localStorage", error);
    }
  }, []);

  useEffect(() => {
    selectedImageIdRef.current = selectedImage?.id || "";
  }, [selectedImage]);

  const loadApprovedImages = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
      setLoadError("");
    }

    try {
      const requestUrl = apiUrl("/api/gallery/images");
      console.log("[gallery] loading approved media", { requestUrl });
      const response = await fetch(requestUrl);
      const result = await response.json().catch(() => ({}));
      console.log("[gallery] approved media response", {
        ok: response.ok,
        status: response.status,
        success: result?.success,
        count: result?.images?.length || 0,
        ids: (result?.images || []).map((item) => item.id),
        items: (result?.images || []).map((item) => ({
          id: item.id,
          mediaType: item.mediaType,
          status: item.status,
          originalFilename: item.originalFilename,
          url: item.url,
          thumbnailUrl: item.thumbnailUrl,
        })),
      });
      if (!response.ok || !result.success) {
        throw new Error(result.detail || result.message || t("gallery.loadError"));
      }
      const nextImages = result.images || [];
      setApprovedImages((currentImages) => {
        if (pendingUploadIds.length > 0) {
          const currentApprovedIds = new Set(currentImages.map((item) => item.id));
          const nowApprovedIds = nextImages
            .map((item) => item.id)
            .filter((id) => pendingUploadIds.includes(id) && !currentApprovedIds.has(id));

          if (nowApprovedIds.length > 0) {
            setNewlyApprovedIds((current) => [...new Set([...current, ...nowApprovedIds])]);
            setApprovalFeedback({
              type: "success",
              message:
                nowApprovedIds.length > 1
                  ? t("gallery.approvalLiveMany").replace("{count}", String(nowApprovedIds.length))
                  : t("gallery.approvalLiveOne"),
            });
            setPendingUploadIds((currentPending) =>
              currentPending.filter((id) => !nowApprovedIds.includes(id))
            );
            imageUploadRef.current?.clearUploadFeedback?.();
          }
        }

        return nextImages;
      });
    } catch (error) {
      console.error("Gallery load error:", error);
      if (!silent) {
        setLoadError(error.message || t("gallery.loadError"));
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [pendingUploadIds, t]);

  useEffect(() => {
    loadApprovedImages();
  }, [loadApprovedImages]);

  useEffect(() => {
    let isCancelled = false;

    const connect = () => {
      const socket = new WebSocket(
        wsUrl(`/ws/gallery?session_id=${encodeURIComponent(galleryViewerSessionIdRef.current)}`)
      );
      websocketRef.current = socket;

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          console.log("[gallery] websocket event", payload);
          if (payload?.type !== "gallery-approved") {
            return;
          }

          const approvedIds = payload.imageIds || [];
          if (approvedIds.length > 0) {
            setNewlyApprovedIds((current) => [...new Set([...current, ...approvedIds])]);
            setPendingUploadIds((current) => current.filter((id) => !approvedIds.includes(id)));
            setApprovalFeedback({
              type: "success",
              message:
                approvedIds.length > 1
                  ? t("gallery.approvalLiveMany").replace("{count}", String(approvedIds.length))
                  : t("gallery.approvalLiveOne"),
            });
            imageUploadRef.current?.clearUploadFeedback?.();
          }
          loadApprovedImages({ silent: true });
        } catch (error) {
          console.error("[gallery] websocket parse error", error);
        }
      };

      socket.onclose = () => {
        if (isCancelled) {
          return;
        }
        reconnectTimeoutRef.current = window.setTimeout(connect, 2500);
      };
    };

    connect();

    return () => {
      isCancelled = true;
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
      }
      websocketRef.current?.close();
    };
  }, [loadApprovedImages, t]);

  useEffect(() => {
    if (pendingUploadIds.length === 0) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== "visible") {
        return;
      }
      loadApprovedImages({ silent: true });
    }, 6000);

    return () => window.clearInterval(intervalId);
  }, [loadApprovedImages, pendingUploadIds]);

  useEffect(() => {
    const syncViewport = () => {
      const { innerWidth } = window;
      const isPhoneViewport = innerWidth <= 768;
      setIsMobileViewport(isPhoneViewport);
      setShowsMobileDock(innerWidth <= 1024);
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, []);

  const mediaItems = approvedImages;

  useEffect(() => {
    console.log("[gallery] rendering media items", {
      count: mediaItems.length,
      ids: mediaItems.map((item) => item.id),
      items: mediaItems.map((item) => ({
        id: item.id,
        mediaType: item.mediaType,
        status: item.status,
        originalFilename: item.originalFilename,
      })),
    });
  }, [mediaItems]);

  useEffect(() => {
    if (!approvalFeedback) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setApprovalFeedback(null);
    }, 4200);

    return () => window.clearTimeout(timeoutId);
  }, [approvalFeedback]);

  useEffect(() => {
    if (newlyApprovedIds.length === 0) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setNewlyApprovedIds([]);
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [newlyApprovedIds]);

  const markFullscreenImageVariantLoaded = useCallback((mediaId, variantName) => {
    setLoadedFullscreenImageVariants((current) => {
      const currentVariants = current[mediaId] || {};
      if (currentVariants[variantName]) {
        return current;
      }
      return {
        ...current,
        [mediaId]: {
          ...currentVariants,
          [variantName]: true,
        },
      };
    });
  }, []);

  const getImageVariantUrl = useCallback((item, variantName) => {
    if (!item) {
      return "";
    }
    return item.imageVariants?.[variantName] || item.url || "";
  }, []);

  const getBestLoadedFullscreenImageVariant = useCallback((item) => {
    if (!item || item.mediaType !== "image") {
      return "";
    }
    const loadedVariants = loadedFullscreenImageVariants[item.id] || {};
    return [...FULLSCREEN_IMAGE_VARIANT_ORDER].reverse().find(
      (variantName) => loadedVariants[variantName] && getImageVariantUrl(item, variantName)
    ) || "";
  }, [getImageVariantUrl, loadedFullscreenImageVariants]);

  const pauseThumbnailVideos = () => {
    const playingVideos = Array.from(
      document.querySelectorAll(".gallery-grid video")
    ).filter((video) => !video.paused && !video.ended);

    pausedThumbnailVideosRef.current = playingVideos;
    playingVideos.forEach((video) => video.pause());
  };

  const resumeThumbnailVideos = () => {
    const videosToResume = pausedThumbnailVideosRef.current;
    pausedThumbnailVideosRef.current = [];
    videosToResume.forEach((video) => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    });
  };

  const handleImageClick = (item) => {
    if (item.mediaType === "video") {
      pauseThumbnailVideos();
      setIsModalVideoMuted(false);
    }
    setSwipeDirection(0);
    setSelectedImage(item);
    setIsClosing(false);
  };

  const closeModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedImage(null);
      setIsClosing(false);
      setSwipeDirection(0);
      resumeThumbnailVideos();
    }, 300);
  };

  const handleModalVideoMuteToggle = (event) => {
    event.stopPropagation();
    const video = modalVideoRef.current;
    if (!video) {
      return;
    }

    video.muted = !video.muted;
    setIsModalVideoMuted(video.muted);
  };

  const handleModalVideoLoadedMetadata = () => {
    const video = modalVideoRef.current;
    if (!video) {
      return;
    }

    setIsModalVideoMuted(video.muted);
  };

  const resetFullscreenVideoState = useCallback((mediaId = "") => {
    setFullscreenVideoState({
      mediaId,
      status: mediaId ? "loading" : "idle",
      error: "",
      bufferedPercent: 0,
    });
  }, []);

  const syncFullscreenVideoBufferState = useCallback((mediaId) => {
    const video = modalVideoRef.current;
    if (!video || !mediaId || selectedImageIdRef.current !== mediaId) {
      return;
    }

    const bufferedPercent = readVideoBufferedPercent(video);
    setFullscreenVideoState((current) => {
      if (current.mediaId !== mediaId || current.bufferedPercent === bufferedPercent) {
        return current;
      }
      return {
        ...current,
        bufferedPercent,
      };
    });
  }, []);

  const updateFullscreenVideoState = useCallback((mediaId, nextStatus, nextError = "") => {
    if (!mediaId || selectedImageIdRef.current !== mediaId) {
      return;
    }

    setFullscreenVideoState((current) => {
      if (
        current.mediaId === mediaId &&
        current.status === nextStatus &&
        current.error === nextError
      ) {
        return current;
      }

      return {
        mediaId,
        status: nextStatus,
        error: nextError,
        bufferedPercent: current.mediaId === mediaId ? current.bufferedPercent : 0,
      };
    });
  }, []);

  const attemptModalVideoPlayback = useCallback(async (mediaId) => {
    const video = modalVideoRef.current;
    if (!video || !mediaId || selectedImageIdRef.current !== mediaId) {
      return;
    }

    try {
      const playResult = video.play();
      if (playResult && typeof playResult.then === "function") {
        await playResult;
      }
      updateFullscreenVideoState(mediaId, video.readyState >= 3 ? "playable" : "loading");
    } catch (error) {
      console.warn("[gallery] fullscreen video autoplay blocked", error);
      updateFullscreenVideoState(mediaId, "autoplay-blocked");
    }
  }, [updateFullscreenVideoState]);

  const handleModalVideoRetry = useCallback(async (event) => {
    event.stopPropagation();

    const mediaId = selectedImage?.id;
    const video = modalVideoRef.current;
    if (!video || !mediaId) {
      return;
    }

    resetFullscreenVideoState(mediaId);
    video.load();
    await attemptModalVideoPlayback(mediaId);
  }, [attemptModalVideoPlayback, resetFullscreenVideoState, selectedImage]);

  const getVideoPlaybackUrl = useCallback((item, quality = "preview") => {
    if (!item || item.mediaType !== "video") {
      return item?.url || "";
    }
    return item.videoVariants?.[quality] || item.videoVariants?.preview || item.url || "";
  }, []);

  const preloadFullscreenMedia = useCallback((item) => {
    if (!item) {
      return;
    }

    if (item.mediaType === "image") {
      FULLSCREEN_IMAGE_VARIANT_ORDER.forEach((variantName) => {
        const variantUrl = getImageVariantUrl(item, variantName);
        if (!variantUrl || loadedFullscreenImageVariants[item.id]?.[variantName]) {
          return;
        }
        const preloader = new Image();
        preloader.decoding = "async";
        preloader.onload = async () => {
          try {
            if (typeof preloader.decode === "function") {
              await preloader.decode();
            }
          } catch (error) {
            // Ignore decode failures and fall back to the loaded asset.
          }
          markFullscreenImageVariantLoaded(item.id, variantName);
        };
        preloader.src = variantUrl;
      });
      return;
    }
  }, [getImageVariantUrl, loadedFullscreenImageVariants, markFullscreenImageVariantLoaded]);

  const galleryGridClassName = `gallery-grid${isMobileViewport ? ` gallery-grid-mobile-${mobileLayout}` : ""}`;
  const selectedImageIndex = selectedImage
    ? mediaItems.findIndex((item) => item.id === selectedImage.id)
    : -1;
  const selectedFullscreenImageVariant = selectedImage?.mediaType === "image"
    ? getBestLoadedFullscreenImageVariant(selectedImage)
    : "";
  const selectedFullscreenImageSrc = selectedImage?.mediaType === "image"
    ? (
        selectedFullscreenImageVariant
          ? getImageVariantUrl(selectedImage, selectedFullscreenImageVariant)
          : (selectedImage.thumbnailUrl || getImageVariantUrl(selectedImage, "small") || selectedImage.url)
      )
    : "";
  const selectedFullscreenVideoSrc = selectedImage?.mediaType === "video"
    ? getVideoPlaybackUrl(selectedImage)
    : "";
  const activeFullscreenVideoState = selectedImage?.mediaType === "video" &&
    fullscreenVideoState.mediaId === selectedImage.id
    ? fullscreenVideoState
    : { mediaId: "", status: "idle", error: "", bufferedPercent: 0 };
  const shouldShowFullscreenVideoPoster = selectedImage?.mediaType === "video"
    ? !["playable", "playing", "buffering"].includes(activeFullscreenVideoState.status)
    : false;
  const shouldShowFullscreenVideoOverlay = selectedImage?.mediaType === "video"
    ? ["loading", "autoplay-blocked", "buffering", "error"].includes(activeFullscreenVideoState.status)
    : false;

  const showImageAtIndex = useCallback((nextIndex, direction = 0) => {
    const boundedIndex = (nextIndex + mediaItems.length) % mediaItems.length;
    const nextItem = mediaItems[boundedIndex];
    if (!nextItem) {
      return;
    }
    setSwipeDirection(direction);
    if (nextItem.mediaType === "video") {
      setIsModalVideoMuted(false);
    }
    setSelectedImage(nextItem);
    setIsClosing(false);
  }, [mediaItems]);

  useEffect(() => {
    if (!selectedImage) {
      return;
    }

    if (selectedImage.mediaType === "video") {
      resetFullscreenVideoState(selectedImage.id);
    } else {
      resetFullscreenVideoState("");
    }

    preloadFullscreenMedia(selectedImage);
    if (mediaItems.length <= 1) {
      return;
    }

    const currentIndex = mediaItems.findIndex((item) => item.id === selectedImage.id);
    if (currentIndex === -1) {
      return;
    }

    preloadFullscreenMedia(mediaItems[(currentIndex + 1) % mediaItems.length]);
    preloadFullscreenMedia(mediaItems[(currentIndex - 1 + mediaItems.length) % mediaItems.length]);
  }, [mediaItems, preloadFullscreenMedia, resetFullscreenVideoState, selectedImage]);

  useEffect(() => {
    if (!selectedImage || selectedImage.mediaType !== "video") {
      return undefined;
    }

    const mediaId = selectedImage.id;
    const playbackTimeoutId = window.setTimeout(() => {
      attemptModalVideoPlayback(mediaId);
    }, 0);

    return () => {
      window.clearTimeout(playbackTimeoutId);
    };
  }, [attemptModalVideoPlayback, selectedImage]);

  useEffect(() => {
    const rootElement = document.documentElement;
    const bodyElement = document.body;
    const isFullscreenMediaOpen = Boolean(selectedImage);
    rootElement.style.overflow = isFullscreenMediaOpen ? "hidden" : "";
    bodyElement.style.overflow = isFullscreenMediaOpen ? "hidden" : "";

    return () => {
      rootElement.style.overflow = "";
      bodyElement.style.overflow = "";
    };
  }, [selectedImage]);

  const handleSwipeDragEnd = (_, info) => {
    if (!isMobileViewport || mediaItems.length <= 1 || selectedImageIndex === -1) {
      return;
    }

    const swipePower = Math.abs(info.offset.x) * Math.max(Math.abs(info.velocity.x), 1);
    if (info.offset.x <= -70 || (info.velocity.x < -400 && swipePower > 120)) {
      showImageAtIndex(selectedImageIndex + 1, 1);
      return;
    }

    if (info.offset.x >= 70 || (info.velocity.x > 400 && swipePower > 120)) {
      showImageAtIndex(selectedImageIndex - 1, -1);
    }
  };

  const handleUploadComplete = useCallback((result) => {
    const uploadedIds = result?.uploadedIds || [];
    if (uploadedIds.length > 0) {
      setPendingUploadIds((current) => [...new Set([...current, ...uploadedIds])]);
      setApprovalFeedback({
        type: "info",
        message:
          uploadedIds.length > 1
            ? t("gallery.approvalPendingMany").replace("{count}", String(uploadedIds.length))
            : t("gallery.approvalPendingOne"),
      });
    }
    loadApprovedImages({ silent: true });
  }, [loadApprovedImages, t]);

  return (
    <div className={`gallery-container${showsMobileDock ? " gallery-container-with-dock" : ""}`}>
      <div className="gallery-topbar">
        <LanguageSwitcher />
        <h1 className="gallery-couple-title">
          {t("janinaCharles.janina")}<br />
          {t("janinaCharles.charles")}
        </h1>
      </div>

      <ImageUpload
        ref={imageUploadRef}
        onUploadComplete={handleUploadComplete}
        isMobileViewport={isMobileViewport}
        hasDockActions={showsMobileDock}
        viewerSessionId={galleryViewerSessionIdRef.current}
      />

      {loadError && <p className="gallery-feedback gallery-feedback-error">{loadError}</p>}
      {approvalFeedback ? (
        <p className={`gallery-feedback gallery-feedback-${approvalFeedback.type}`}>
          {approvalFeedback.message}
        </p>
      ) : null}

      {isLoading ? (
        <div className="gallery-loading" aria-label={t("gallery.loading")} role="status">
          <span className="gallery-loading-spinner" aria-hidden="true" />
        </div>
      ) : mediaItems.length > 0 ? (
        <motion.div
          layout
          transition={{ layout: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } }}
          className={galleryGridClassName}
        >
          {mediaItems.map((item, index) => (
            <motion.button
              layout
              transition={{ layout: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } }}
              key={`${item.id}-${index}`}
              type="button"
              className="gallery-media-button"
              onClick={() => handleImageClick(item)}
            >
              {item.mediaType === "video" ? (
                <video
                  src={getVideoPlaybackUrl(item, "preview")}
                  poster={item.thumbnailUrl || undefined}
                  aria-label={t("gallery.alt").replace("{number}", String(index + 1))}
                  className={`gallery-image gallery-video-thumbnail${newlyApprovedIds.includes(item.id) ? " gallery-image-new" : ""}`}
                  muted
                  playsInline
                  loop
                  autoPlay
                  preload="metadata"
                />
              ) : (
                <LazyGalleryImage
                  src={item.thumbnailUrl || item.url}
                  alt={t("gallery.alt").replace("{number}", String(index + 1))}
                  className={`gallery-image${newlyApprovedIds.includes(item.id) ? " gallery-image-new" : ""}`}
                  eager={index < 8}
                />
              )}
            </motion.button>
          ))}
        </motion.div>
      ) : (
        <p className="gallery-feedback gallery-feedback-info">{t("gallery.empty")}</p>
      )}

      {selectedImage && (
        <div
          className={`image-modal ${selectedImage.mediaType === "video" ? "image-modal-video" : ""} ${isClosing ? "closing" : ""}`}
          onClick={closeModal}
        >
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            {showsMobileDock ? (
              <button
                type="button"
                className="modal-close"
                onClick={(event) => {
                  event.stopPropagation();
                  closeModal();
                }}
                aria-label="Close fullscreen media"
                title="Close"
              >
                <X size={20} strokeWidth={2.2} />
              </button>
            ) : null}
            {isMobileViewport && mediaItems.length > 1 ? (
              <>
                <button
                  type="button"
                  className="modal-nav-button modal-nav-button-left"
                  onClick={() => showImageAtIndex(selectedImageIndex - 1, -1)}
                  aria-label={t("gallery.previous")}
                  title={t("gallery.previous")}
                >
                  <ChevronLeft size={22} strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  className="modal-nav-button modal-nav-button-right"
                  onClick={() => showImageAtIndex(selectedImageIndex + 1, 1)}
                  aria-label={t("gallery.next")}
                  title={t("gallery.next")}
                >
                  <ChevronRight size={22} strokeWidth={2.25} />
                </button>
              </>
            ) : null}
            <AnimatePresence initial={false} mode="wait" custom={swipeDirection}>
              <motion.div
                key={selectedImage.id}
                custom={swipeDirection}
                className="modal-swipe-card"
                drag={isMobileViewport && mediaItems.length > 1 ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.18}
                onDragEnd={handleSwipeDragEnd}
                variants={{
                  enter: (direction) => ({
                    x: direction > 0 ? 120 : direction < 0 ? -120 : 0,
                    rotate: direction > 0 ? 6 : direction < 0 ? -6 : 0,
                    opacity: 0,
                  }),
                  center: {
                    x: 0,
                    rotate: 0,
                    opacity: 1,
                  },
                  exit: (direction) => ({
                    x: direction > 0 ? -120 : direction < 0 ? 120 : 0,
                    rotate: direction > 0 ? -6 : direction < 0 ? 6 : 0,
                    opacity: 0,
                  }),
                }}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
                {selectedImage.mediaType === "video" ? (
                  <div className="modal-video-shell">
                    <img
                      src={selectedImage.thumbnailUrl || selectedImage.url}
                      alt={t("gallery.enlarged")}
                      className={`modal-image modal-image-thumbnail${shouldShowFullscreenVideoPoster ? "" : " is-hidden"}`}
                      decoding="async"
                    />
                    <video
                      ref={modalVideoRef}
                      src={selectedFullscreenVideoSrc}
                      className={`modal-image modal-video modal-full-media${shouldShowFullscreenVideoPoster ? "" : " is-loaded"}`}
                      loop
                      playsInline
                      preload="auto"
                      muted={isModalVideoMuted}
                      onLoadedMetadata={handleModalVideoLoadedMetadata}
                      onLoadedData={() => {
                        syncFullscreenVideoBufferState(selectedImage.id);
                        updateFullscreenVideoState(selectedImage.id, "playable");
                      }}
                      onDurationChange={() => syncFullscreenVideoBufferState(selectedImage.id)}
                      onProgress={() => syncFullscreenVideoBufferState(selectedImage.id)}
                      onCanPlay={() => {
                        syncFullscreenVideoBufferState(selectedImage.id);
                        updateFullscreenVideoState(selectedImage.id, "playable");
                      }}
                      onPlaying={() => {
                        syncFullscreenVideoBufferState(selectedImage.id);
                        updateFullscreenVideoState(selectedImage.id, "playing");
                      }}
                      onWaiting={() => {
                        syncFullscreenVideoBufferState(selectedImage.id);
                        updateFullscreenVideoState(selectedImage.id, "buffering");
                      }}
                      onStalled={() => {
                        syncFullscreenVideoBufferState(selectedImage.id);
                        updateFullscreenVideoState(selectedImage.id, "buffering");
                      }}
                      onSuspend={() => {
                        syncFullscreenVideoBufferState(selectedImage.id);
                        if (modalVideoRef.current?.readyState < 3) {
                          updateFullscreenVideoState(selectedImage.id, "buffering");
                        }
                      }}
                      onVolumeChange={() => {
                        if (modalVideoRef.current) {
                          setIsModalVideoMuted(modalVideoRef.current.muted);
                        }
                      }}
                      onError={() => updateFullscreenVideoState(selectedImage.id, "error", t("gallery.videoError"))}
                    />
                    {shouldShowFullscreenVideoOverlay ? (
                      <div className={`modal-video-overlay modal-video-overlay-${activeFullscreenVideoState.status}`}>
                        <div className="modal-video-status-core">
                          {activeFullscreenVideoState.status === "loading" ? (
                            <>
                              <span className="modal-video-spinner" aria-hidden="true" />
                              <p className="modal-video-status-text">{t("gallery.videoLoading")}</p>
                            </>
                          ) : null}
                          {activeFullscreenVideoState.status === "buffering" ? (
                            <>
                              <span className="modal-video-spinner" aria-hidden="true" />
                              <p className="modal-video-status-text">{t("gallery.videoBuffering")}</p>
                              <button
                                type="button"
                                className="modal-video-action-button"
                                onClick={handleModalVideoRetry}
                              >
                                {t("gallery.videoRetry")}
                              </button>
                            </>
                          ) : null}
                          {activeFullscreenVideoState.status === "autoplay-blocked" ? (
                            <>
                              <p className="modal-video-status-text">{t("gallery.videoAutoplayBlocked")}</p>
                              <button
                                type="button"
                                className="modal-video-action-button"
                                onClick={handleModalVideoRetry}
                              >
                                {t("gallery.playVideo")}
                              </button>
                            </>
                          ) : null}
                          {activeFullscreenVideoState.status === "error" ? (
                            <>
                              <p className="modal-video-status-text">{activeFullscreenVideoState.error || t("gallery.videoError")}</p>
                              <button
                                type="button"
                                className="modal-video-action-button"
                                onClick={handleModalVideoRetry}
                              >
                                {t("gallery.videoRetry")}
                              </button>
                            </>
                          ) : null}
                        </div>
                        {(activeFullscreenVideoState.status === "loading" || activeFullscreenVideoState.status === "buffering") ? (
                          <div className="modal-video-buffer-dock">
                            <div className="modal-video-buffer-progress" aria-hidden="true">
                              <span
                                className="modal-video-buffer-progress-fill"
                                style={{ width: `${activeFullscreenVideoState.bufferedPercent}%` }}
                              />
                            </div>
                            <p className="modal-video-buffer-progress-text">
                              {Math.round(activeFullscreenVideoState.bufferedPercent)}%
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="modal-video-sound-toggle"
                      onClick={handleModalVideoMuteToggle}
                      aria-label={isModalVideoMuted ? t("gallery.unmuteVideo") : t("gallery.muteVideo")}
                      title={isModalVideoMuted ? t("gallery.unmuteVideo") : t("gallery.muteVideo")}
                    >
                      {isModalVideoMuted ? <VolumeX size={18} strokeWidth={2} /> : <Volume2 size={18} strokeWidth={2} />}
                    </button>
                  </div>
                ) : (
                  <div className="modal-image-shell">
                    <img
                      src={selectedFullscreenImageSrc}
                      alt={t("gallery.enlarged")}
                      className="modal-image"
                      decoding="async"
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showsMobileDock && !selectedImage && (
          <motion.div
            className="gallery-layout-dock"
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              layout
              className="gallery-layout-toggle"
              role="group"
              aria-label={t("gallery.layoutToggle")}
            >
              {isMobileViewport ? (
                [
                  {
                    value: "single",
                    label: t("gallery.layoutSingle"),
                    icon: Columns2,
                  },
                  {
                    value: "grid",
                    label: t("gallery.layoutGrid"),
                    icon: Grid3X3,
                  },
                ].map(({ value, label, icon: Icon }) => (
                  <motion.button
                    layout
                    key={value}
                    type="button"
                    className={`gallery-layout-toggle-button${mobileLayout === value ? " active" : ""}`}
                    onClick={() => setMobileLayout(value)}
                    aria-label={label}
                    title={label}
                    whileTap={{ scale: 0.94 }}
                  >
                    {mobileLayout === value ? (
                      <motion.span
                        layoutId="gallery-layout-pill"
                        className="gallery-layout-toggle-pill"
                        transition={{ type: "spring", stiffness: 500, damping: 35 }}
                      />
                    ) : null}
                    <Icon size={18} strokeWidth={2} />
                  </motion.button>
                ))
              ) : (
                <motion.button
                  layout
                  type="button"
                  className="gallery-layout-toggle-button active"
                  aria-label={t("gallery.layoutGrid")}
                  title={t("gallery.layoutGrid")}
                  whileTap={{ scale: 0.94 }}
                >
                  <span className="gallery-layout-toggle-pill" />
                  <Grid3X3 size={18} strokeWidth={2} />
                </motion.button>
              )}
              <motion.button
                layout
                type="button"
                className="gallery-layout-toggle-button gallery-layout-action-button"
                onClick={() => imageUploadRef.current?.openLibraryPicker()}
                aria-label={t("imageUpload.openLibrary")}
                title={t("imageUpload.openLibrary")}
                whileTap={{ scale: 0.94 }}
              >
                <Images size={18} strokeWidth={2} />
              </motion.button>
              <motion.button
                layout
                type="button"
                className="gallery-layout-toggle-button gallery-layout-action-button"
                onClick={() => imageUploadRef.current?.openCameraPicker()}
                aria-label={t("imageUpload.openCamera")}
                title={t("imageUpload.openCamera")}
                whileTap={{ scale: 0.94 }}
              >
                <Camera size={18} strokeWidth={2} />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Gallery;
